import { useEffect, useMemo, useRef, useState } from "react";
import { PageMeta } from "@/components/PageMeta";
import { ROUTES } from "@/config/links";
import { useLang } from "@/i18n";
import {
  capture, generateDiagnosticRunId, trackAssetViewed, trackDiagnosticStarted,
  trackDiagnosticQuestionsCompleted, trackDiagnosticCompleted, trackResultsViewed,
  buildAttributionRequestContext,
} from "@/lib/analytics";
import { DiagnosticLayout } from "@/components/diagnostics/DiagnosticLayout";
import content from "./content.json";
import { IntroScreen } from "./IntroScreen";
import { QuestionScreen } from "./QuestionScreen";
import { ResultScreen } from "./ResultScreen";
import { buildReportModel } from "./reportModel";
import { loadProgress, saveProgress, clearProgress } from "./progressStorage";
import { downloadActionPlanText } from "./textExport";
import { downloadReportPdf } from "./api";
import { requestPlaybook } from "./playbookApi";
import "./presenterArchetype.css";

const TOTAL_QUESTIONS = content.content.en.questions.length;
const EMPTY_ANSWERS = () => Array(TOTAL_QUESTIONS).fill(null);
const DIAGNOSTIC_ID = "presenter_archetype";

/**
 * Public tool, listed on the Tools page (`insights/tools`) alongside the
 * other two diagnostics — tripled under `/`, `/pt/`, `/el/` like them, so
 * `lang` (site locale) drives which language's content renders; no more
 * in-page language switcher (that lived in the now-removed private-preview
 * version). Flow: intro (+ resume) → one question at a time → tie-aware
 * results, with optional email capture, direct PDF download, text
 * download, browser print fallback, and a shareable result image.
 *
 * InterAct Tracking Foundation milestone (2026-10-09) — the full P0
 * local-result funnel (`asset_viewed`/`diagnostic_started`/
 * `diagnostic_questions_completed`/`diagnostic_completed`/
 * `results_viewed`), via the SAME shared `analytics.js` helpers the
 * other 2 diagnostics use (`asset_id: "presenter_archetype"`
 * discriminates — note these 5 use `asset_id`, matching the shared
 * schema, while the pre-existing `diagnostic_abandoned` below keeps
 * its own established `diagnostic_id` key, matching how the other 2
 * diagnostics' own `diagnostic_abandoned` is already shaped). A
 * client-generated, stable `run_id` (`runIdRef`) is created once per
 * NEW attempt (`handleStart`), preserved across a resume (persisted
 * into the SAME localStorage progress blob `progressStorage.js`
 * already saves) and an edit-answers round-trip, and replaced with a
 * fresh one only on a genuine retake. `diagnostic_completed` is
 * BROWSER-owned here (no backend run exists for this asset) — the
 * sole, authoritative publisher, never emitted alongside a backend
 * one. `lead_capture_completed`/`lead_created` are NEVER emitted from
 * this local result — only `routers/presenter_playbook_routes.py`
 * (the separate, optional, email-gated Presenter Playbook request
 * below) can produce those, backend-side, on an accepted request.
 *
 * Everything else here uses a pre-existing `presenter_archetype_*`-
 * prefixed name, untouched by this milestone.
 */
export const PresenterArchetype = () => {
  const { lang } = useLang();
  const [stage, setStage] = useState("intro"); // intro | running | result
  const [questionIndex, setQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState(EMPTY_ANSWERS);
  const [savedProgress, setSavedProgress] = useState(null);
  const [requestId, setRequestId] = useState(null);
  const [storageOk, setStorageOk] = useState(true);
  const startedAtRef = useRef(null);
  const abandonedFiredRef = useRef(false);
  // Stable client-generated run_id (tracking_registry.json: Presenter
  // Archetype has completion_mode=local_result, no backend run) — one
  // per NEW assessment attempt (handleStart), PRESERVED across a
  // resume (restored from the same localStorage progress blob
  // run_id was saved into) and across an edit-answers round-trip
  // (same attempt, not regenerated), and replaced with a fresh one
  // only on a genuine retake (handleRetake -> intro -> handleStart).
  const runIdRef = useRef(null);
  // Guards diagnostic_questions_completed/diagnostic_completed so
  // they fire EXACTLY once per run_id, even if the visitor edits
  // answers and revisits the result screen for the SAME run.
  const completionFiredRunIdRef = useRef(null);
  // InterAct Tracking Foundation milestone (2026-10-10) — the CTA
  // that led here (if any), captured one-shot at this SAME mount-time
  // `trackAssetViewed` call, for the eventual Playbook request below.
  const ctaNavContextRef = useRef(null);

  useEffect(() => {
    ctaNavContextRef.current = trackAssetViewed(DIAGNOSTIC_ID);
  }, []);

  useEffect(() => {
    const saved = loadProgress();
    if (saved && saved.answers?.length === TOTAL_QUESTIONS) {
      setSavedProgress(saved);
    }
  }, []);

  useEffect(() => {
    if (stage === "running") {
      setStorageOk(saveProgress({ language: lang, questionIndex, answers, runId: runIdRef.current }));
    }
  }, [stage, lang, questionIndex, answers]);

  useEffect(() => {
    if (stage === "result") {
      trackResultsViewed({ assetId: DIAGNOSTIC_ID, runId: runIdRef.current });
    }
  }, [stage]);

  const model = useMemo(() => {
    if (stage !== "result") return null;
    return buildReportModel(answers, lang);
  }, [stage, answers, lang]);

  /**
   * "Diagnostic abandoned" — same `pagehide`-based pattern as the
   * other 2 diagnostics (2026-10-21 fix — see
   * `NextPresentationReadinessCheck/index.jsx`'s identical comment
   * for the full incident writeup). Fires once per run, only when the
   * page is actually being unloaded, never merely backgrounded.
   */
  const buildAbandonedProps = (reason) => ({
    diagnostic_id: DIAGNOSTIC_ID,
    run_id: runIdRef.current,
    language: lang,
    stage_at_exit: stage,
    question_index: stage === "running" ? questionIndex : null,
    question_total: TOTAL_QUESTIONS,
    progress_percent: Math.round((questionIndex / TOTAL_QUESTIONS) * 100),
    page_path: window.location.pathname,
    time_spent_ms: startedAtRef.current ? Date.now() - startedAtRef.current : null,
    reason,
  });

  useEffect(() => {
    if (stage !== "running") return;
    const onPageHide = () => {
      if (abandonedFiredRef.current) return;
      abandonedFiredRef.current = true;
      capture("diagnostic_abandoned", buildAbandonedProps("page_hidden"));
    };
    window.addEventListener("pagehide", onPageHide);
    return () => window.removeEventListener("pagehide", onPageHide);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, questionIndex, lang]);

  const handleStart = () => {
    setAnswers(EMPTY_ANSWERS());
    setQuestionIndex(0);
    setSavedProgress(null);
    setStage("running");
    startedAtRef.current = Date.now();
    abandonedFiredRef.current = false;
    runIdRef.current = generateDiagnosticRunId();
    trackDiagnosticStarted({ assetId: DIAGNOSTIC_ID, runId: runIdRef.current });
  };

  const handleResume = () => {
    setAnswers(savedProgress.answers);
    setQuestionIndex(Math.min(savedProgress.questionIndex || 0, TOTAL_QUESTIONS - 1));
    setSavedProgress(null);
    setStage("running");
    startedAtRef.current = Date.now();
    abandonedFiredRef.current = false;
    // Preserve the SAME run_id across the resume (never regenerate) —
    // falls back to a fresh id only for a progress blob saved before
    // this milestone added runId, so stale localStorage can't crash this.
    runIdRef.current = savedProgress.runId || generateDiagnosticRunId();
    capture("presenter_archetype_resumed", { language: lang, question_index: savedProgress.questionIndex || 0, run_id: runIdRef.current });
  };

  const handleStartFresh = () => {
    clearProgress();
    setSavedProgress(null);
    capture("presenter_archetype_start_fresh", { language: lang });
  };

  const handleSelect = (optionId) => {
    setAnswers((prev) => {
      const next = [...prev];
      next[questionIndex] = optionId;
      return next;
    });
  };

  // Guards against a double-fire of the Next button (double-click/
  // double-tap) producing two advances for one click — same fix
  // category as the other 2 diagnostics' 2026-10-21 incident, applied
  // here defensively even though this screen never had the timer-
  // based race (it was always explicit-Next already).
  const advancingRef = useRef(false);

  useEffect(() => {
    advancingRef.current = false;
  }, [questionIndex]);

  const handleNext = () => {
    if (!answers[questionIndex] || advancingRef.current) return;
    advancingRef.current = true;
    capture("presenter_archetype_question_answered", {
      language: lang,
      question_index: questionIndex,
      question_total: TOTAL_QUESTIONS,
      archetype: answers[questionIndex],
    });
    if (questionIndex < TOTAL_QUESTIONS - 1) {
      setQuestionIndex((i) => i + 1);
      return;
    }
    setRequestId(crypto.randomUUID());
    clearProgress();
    setStage("result");
    // diagnostic_questions_completed/diagnostic_completed are exactly
    // ONE canonical event per run_id (idempotency_scope: diagnostic_id
    // + run_id) — guarded so re-answering via "edit answers" and
    // reaching this SAME run's result screen again never re-fires
    // either. 2026-10-09 correction: the extra leaders/is_blend/
    // time_spent_ms result detail that used to ride along in a
    // separate `presenter_archetype_result_computed` event was
    // REMOVED — that event was never defined in asset_registry.json
    // with an approved, PII-free property contract. The result itself
    // is still fully available to the app via `model` (the useMemo
    // above, used by ResultScreen) — this only removes the PostHog-
    // bound analytics event, not the underlying data.
    if (completionFiredRunIdRef.current !== runIdRef.current) {
      completionFiredRunIdRef.current = runIdRef.current;
      trackDiagnosticQuestionsCompleted({ assetId: DIAGNOSTIC_ID, runId: runIdRef.current });
      trackDiagnosticCompleted({ assetId: DIAGNOSTIC_ID, runId: runIdRef.current });
    }
  };

  const handleBack = () => {
    setQuestionIndex((i) => Math.max(0, i - 1));
    capture("presenter_archetype_back", { language: lang, question_index: questionIndex });
  };

  const handleExit = () => {
    if (!abandonedFiredRef.current && stage === "running") {
      abandonedFiredRef.current = true;
      capture("diagnostic_abandoned", buildAbandonedProps("exit_button"));
    }
    clearProgress();
    setAnswers(EMPTY_ANSWERS());
    setQuestionIndex(0);
    setStage("intro");
  };

  const handleEditAnswers = () => {
    setQuestionIndex(0);
    setStage("running");
    capture("presenter_archetype_review_answers", { language: lang });
  };

  const handleRetake = () => {
    clearProgress();
    setAnswers(EMPTY_ANSWERS());
    setQuestionIndex(0);
    setStage("intro");
    capture("presenter_archetype_retake", { language: lang });
  };

  const handleRequestPlaybook = async ({ name, email, marketing, gaClientId }) => {
    await requestPlaybook({
      language: lang, answers, email, name, marketing, requestId, gaClientId, runId: runIdRef.current,
      attributionContext: buildAttributionRequestContext({ ctaNavContext: ctaNavContextRef.current, runId: runIdRef.current }),
    });
  };

  const handleDownloadPdf = async () => {
    await downloadReportPdf({ language: lang, answers });
  };

  const handleDownloadText = () => {
    downloadActionPlanText(model);
    capture("presenter_archetype_text_downloaded", { language: lang });
  };

  const handlePrint = () => {
    capture("presenter_archetype_print_clicked", { language: lang });
    window.print();
  };

  return (
    <>
      <PageMeta titleKey="presenterArchetype.title" descriptionKey="presenterArchetype.description" path={ROUTES.presenterArchetype} />
      <DiagnosticLayout showExit={stage === "running"} onExit={handleExit} showLanguagePrompt={stage === "intro"}>
        <div className="min-h-[70vh] bg-[#f6f4ed]" data-testid="pa-app-root">
          {stage === "intro" && (
            <IntroScreen
              language={lang}
              onStart={handleStart}
              savedProgress={savedProgress}
              onResume={handleResume}
              onStartFresh={handleStartFresh}
            />
          )}
          {stage === "running" && (
            <QuestionScreen
              language={lang}
              questionIndex={questionIndex}
              totalQuestions={TOTAL_QUESTIONS}
              selectedOptionId={answers[questionIndex]}
              onSelect={handleSelect}
              onNext={handleNext}
              onBack={handleBack}
              storageOk={storageOk}
            />
          )}
          {stage === "result" && model && (
            <ResultScreen
              model={model}
              onRequestPlaybook={handleRequestPlaybook}
              onDownloadPdf={handleDownloadPdf}
              onDownloadText={handleDownloadText}
              onPrint={handlePrint}
              onEditAnswers={handleEditAnswers}
              onRetake={handleRetake}
              runId={runIdRef.current}
            />
          )}
        </div>
      </DiagnosticLayout>
    </>
  );
};

export default PresenterArchetype;
